import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import PDFPage from './PDFDocument';
import React from 'react';

const FONT_SIZE = 7;
const ROWS_PER_PAGE = 15;

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const styles = StyleSheet.create({
  tableContainer: {
    width: '100%',
    backgroundColor: '#ffffff',
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginVertical: 10,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#1d428a',
  },
  tableCol: {
    flex: 1,
    padding: 2,
    minWidth: 40,
  },
  tableColNarrow: {
    flex: 0.8,
    padding: 2,
    minWidth: 35,
  },
  tableColWide: {
    flex: 1.2,
    padding: 2,
    minWidth: 50,
  },
  tableColSSI1: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    borderLeftWidth: 2,
    borderLeftColor: '#000',
    borderLeftStyle: 'solid',
  },
  tableColSSI2: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    borderRightWidth: 2,
    borderRightColor: '#000',
    borderRightStyle: 'solid',
  },
  tableColSSI1Header: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    borderLeftWidth: 2,
    borderLeftColor: '#000',
    borderLeftStyle: 'solid',
    backgroundColor: 'rgba(140,178,240, 0.6)',
  },
  tableColSSI2Header: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    borderRightWidth: 2,
    borderRightColor: '#000',
    borderRightStyle: 'solid',
    backgroundColor: 'rgba(140,178,240, 0.6)',
  },
  tableColBaseIncome: {
    flex: 1.2,
    padding: 2,
    minWidth: 50,
    backgroundColor: 'rgba(185,208,246, 0.6)',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: '#000',
    borderStyle: 'solid',
  },
  tableColDesiredIncome: {
    flex: 1.2,
    padding: 2,
    minWidth: 50,
    backgroundColor: 'rgba(140,178,240, 0.6)',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: '#000',
    borderStyle: 'solid',
  },
  tableColPlanTaxes: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    backgroundColor: 'rgba(140,178,240, 0.6)',
  },
  tableCellHeader: {
    fontSize: FONT_SIZE,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
  },
  tableCellHeaderSSI: {
    fontSize: FONT_SIZE,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
  },
  tableCell: {
    fontSize: FONT_SIZE,
    textAlign: 'center',
  },
  tableCellCurrency: {
    fontSize: FONT_SIZE,
    textAlign: 'right',
  },
  tableColGapPositive: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    backgroundColor: 'rgba(0, 200, 0, 0.2)',
  },
  tableColGapNegative: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    backgroundColor: 'rgba(255, 0, 0, 0.2)',
  },
  tableColPlanGapPositive: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    backgroundColor: 'rgba(0, 200, 0, 0.5)',
  },
  tableColPlanGapNegative: {
    flex: 1,
    padding: 2,
    minWidth: 40,
    backgroundColor: 'rgba(255, 0, 0, 0.5)',
  },
  tableColTotalPlanIncomePositive: {
    flex: 1.2,
    padding: 2,
    minWidth: 50,
    backgroundColor: 'rgba(0, 200, 0, 0.2)',
  },
  tableColTotalPlanIncomeNegative: {
    flex: 1.2,
    padding: 2,
    minWidth: 50,
    backgroundColor: 'rgba(255, 0, 0, 0.2)',
  },
  imageContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartImage: {
    width: '100%',
    height: 'auto',
    maxWidth: 320,
  },
});

const IncomeByYearPDF = ({ chartImages = {}, clientData, pageNumber, totalPages }) => {
  const projectionData = clientData.incomeByYearData || [];
  const chartImage = chartImages['income-by-year-chart'];

  // Split data into chunks to prevent PDF renderer from hanging
  const dataChunks = [];
  for (let i = 0; i < projectionData.length; i += ROWS_PER_PAGE) {
    dataChunks.push(projectionData.slice(i, i + ROWS_PER_PAGE));
  }

  return (
    <>
      {dataChunks.map((chunk, chunkIndex) => (
        <PDFPage
          key={chunkIndex}
          header={`Income By Year${dataChunks.length > 1 ? ` (${chunkIndex + 1}/${dataChunks.length})` : ''}`}
          pageNumber={pageNumber + chunkIndex}
          totalPages={totalPages}
          showDisclaimer={true}
          showFooter={true}
        >
          <View style={styles.tableContainer}>
            <View style={styles.table}>
              {/* Table Header */}
              <View style={styles.tableHeaderRow}>
                <View style={styles.tableColNarrow}>
                  <Text style={styles.tableCellHeader}>Year</Text>
                </View>
                <View style={styles.tableColNarrow}>
                  <Text style={styles.tableCellHeader}>{clientData.firstname} Age</Text>
                </View>
                <View style={styles.tableColNarrow}>
                  <Text style={styles.tableCellHeader}>{clientData.spouse_firstname} Age</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>{clientData.firstname} W2</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>{clientData.spouse_firstname} W2</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Pension</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Rental</Text>
                </View>
                <View style={styles.tableColSSI1Header}>
                  <Text style={styles.tableCellHeaderSSI}>{clientData.firstname} SSI</Text>
                </View>
                <View style={styles.tableColSSI2Header}>
                  <Text style={styles.tableCellHeaderSSI}>{clientData.spouse_firstname} SSI</Text>
                </View>
                <View style={styles.tableColBaseIncome}>
                  <Text style={styles.tableCellHeader}>Base Income</Text>
                </View>
                <View style={styles.tableColDesiredIncome}>
                  <Text style={styles.tableCellHeader}>Desired Income</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Gap</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Leg 1</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Leg 2</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Leg 3</Text>
                </View>
                <View style={styles.tableColPlanTaxes}>
                  <Text style={styles.tableCellHeader}>Plan Income Taxes</Text>
                </View>
                <View style={styles.tableColWide}>
                  <Text style={styles.tableCellHeader}>Total Planned Income</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCellHeader}>Plan Gap</Text>
                </View>
              </View>

              {/* Table Body */}
              {chunk.map((row, index) => (
                <View key={index} style={[
                  styles.tableRow,
                  { backgroundColor: index % 2 === 0 ? '#fff' : '#eaeaea' }
                ]}>
                  <View style={styles.tableColNarrow}>
                    <Text style={styles.tableCell}>{row.year}</Text>
                  </View>
                  <View style={styles.tableColNarrow}>
                    <Text style={styles.tableCell}>{row.yearAge}</Text>
                  </View>
                  <View style={styles.tableColNarrow}>
                    <Text style={styles.tableCell}>{row.yearSpouseAge}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.yearW2Income || 0)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.yearW22Income || 0)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency((row.yearPensionIncome + row.yearSpousePensionIncome) || 0)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency((row.yearRentalIncome + row.yearSpouseRentalIncome) || 0)}</Text>
                  </View>
                  <View style={styles.tableColSSI1}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.yearSSI || 0)}</Text>
                  </View>
                  <View style={styles.tableColSSI2}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.yearSSI2 || 0)}</Text>
                  </View>
                  <View style={styles.tableColBaseIncome}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.baseIncomeAfterTax)}</Text>
                  </View>
                  <View style={styles.tableColDesiredIncome}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.yearDesiredIncome)}</Text>
                  </View>
                  <View style={row.baseIncomeAfterTax >= row.yearDesiredIncome ? styles.tableColGapPositive : styles.tableColGapNegative}>
                    <Text style={styles.tableCellCurrency}>
                      {row.yearGap > 0 ? '-' : ''}{formatCurrency(Math.abs(row.yearGap) || 0)}
                    </Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.leg1Income || 0)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.leg2Income || 0)}</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.leg3Income || 0)}</Text>
                  </View>
                  <View style={styles.tableColPlanTaxes}>
                    <Text style={styles.tableCellCurrency}>
                      {row.planIncomeTaxes < 0 ? '-' : ''}{formatCurrency(Math.abs(row.planIncomeTaxes))}
                    </Text>
                  </View>
                  <View style={row.totalPlanIncomeAfterTax > row.yearDesiredIncome ? styles.tableColTotalPlanIncomePositive : styles.tableColTotalPlanIncomeNegative}>
                    <Text style={styles.tableCellCurrency}>{formatCurrency(row.totalPlanIncomeAfterTax)}</Text>
                  </View>
                  <View style={row.planGap >= 0 ? styles.tableColPlanGapPositive : styles.tableColPlanGapNegative}>
                    <Text style={styles.tableCellCurrency}>
                      {row.planGap < 0 ? '-' : ''}{formatCurrency(Math.abs(row.planGap))}
                    </Text>
                  </View>
                </View>
              ))}
            </View>

            {/* Show chart only on last page */}
            {chunkIndex === dataChunks.length - 1 && chartImage && (
              <View style={styles.imageContainer}>
                <Image src={chartImage} style={styles.chartImage} />
              </View>
            )}
          </View>
        </PDFPage>
      ))}
    </>
  );
};

export default IncomeByYearPDF;
